import React, { Suspense, useLayoutEffect, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  withRouter,
  useLocation,
} from "react-router-dom";
import ReactGA from "react-ga4";
import { NewsletterFormProvider } from "./components/providers/newsletter-form.context";
import { AppProvider } from "./components/providers/app-context";
import LoadingOverlay, { PageLoader } from "./components/commons/loading-overlay";

// Preloader
const Preloader = React.lazy(() => import("./components/layouts/Preloader"));

// Pages
const Home = React.lazy(() => import("./components/pages/Home"));
const TermsOfService = React.lazy(() =>
  import("./components/pages/TermsOfService")
);
const PrivacyPolicy = React.lazy(() =>
  import("./components/pages/PrivacyPolicy")
);
const Rooms = React.lazy(() => import("./components/pages/Rooms"));
const Contact = React.lazy(() => import("./components/pages/Contact"));
const About = React.lazy(() => import("./components/pages/About"));
const TakeTour = React.lazy(() => import("./components/pages/TakeTour"));

// Scroll to Top
const ScrollToTop = withRouter(({ children, location: { pathname } }) => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return children || null;
});

function ScrollToHashElement() {
  const location = useLocation();

  useEffect(() => {
    // If there's a hash in the URL
    if (location.hash) {
      // Remove the '#' character
      const elementId = location.hash.substring(1);

      // Find the element by ID
      const element = document.getElementById(elementId);

      // If found, scroll to it
      if (element) {
        // Small timeout to ensure the DOM is fully rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } else {
      // If no hash, scroll to top of page
      window.scrollTo(0, 0);
    }
  }, [location]);

  return null; // This component doesn't render anything
}

function App() {
  usePageTracking();
  return (
    <AppProvider>
      <NewsletterFormProvider>
        <Router>
          <ScrollToHashElement />
          <Suspense
            fallback={
              <div className="position-relative">
                <PageLoader />
              </div>
            }
          >
            <ScrollToTop>
              <Preloader />
              <Route path="/" exact component={Home} />
              <Route path="/rooms" exact component={Rooms} />
              <Route path="/contact" exact component={Contact} />
              <Route path="/about" exact component={About} />
              <Route
                path="/terms-of-service"
                exact
                component={TermsOfService}
              />
              <Route path="/privacy-policy" exact component={PrivacyPolicy} />
              <Route path="/request-reservation" exact component={TakeTour} />
            </ScrollToTop>
          </Suspense>
        </Router>
      </NewsletterFormProvider>
    </AppProvider>
  );
}

export default App;

function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
  }, [location]);
}
